import { z } from 'zod';

export const consentChoiceSchema = z.object({
  analytics: z.boolean(), advertising: z.boolean().default(false), functional: z.boolean().default(false),
  source: z.enum(['banner', 'form', 'settings']).default('banner'),
}).refine((value) => !value.advertising || value.analytics, 'Advertising measurement requires analytics consent');
export type ConsentChoice = z.infer<typeof consentChoiceSchema>;
export const consentStateSchema = z.object({ hasDecision: z.boolean(), analytics: z.boolean(), advertising: z.boolean(), functional: z.boolean(), policyVersion: z.string() });
export type ConsentState = z.infer<typeof consentStateSchema>;

// The collector accepts only these dimensions; arbitrary properties or personal data are rejected.
const campaignValue = z.string().trim().min(1).max(200).regex(/^[\p{L}\p{N} _.+%/-]+$/u, 'Use a campaign label without personal data');
const clickId = z.string().trim().min(1).max(200).regex(/^[A-Za-z0-9_-]+$/);
export const publicTouchSchema = z.object({
  utmSource: campaignValue.optional(), utmMedium: campaignValue.optional(), utmCampaign: campaignValue.optional(),
  utmTerm: campaignValue.optional(), utmContent: campaignValue.optional(),
  referrerOrigin: z.url().max(500).optional(),
  gclid: clickId.optional(), gbraid: clickId.optional(), wbraid: clickId.optional(),
}).strict();
export type PublicTouch = z.infer<typeof publicTouchSchema>;
export const publicEventNames = ['page_view', 'landing_view', 'cta_click', 'form_view', 'form_start', 'form_error', 'form_submit'] as const;
export const publicAnalyticsEventSchema = z.object({
  id: z.uuid(), name: z.enum(publicEventNames), version: z.literal(1), action: z.enum(['primary', 'secondary', 'form']).optional(),
  touch: publicTouchSchema.optional(),
}).strict();
export type PublicAnalyticsEvent = z.infer<typeof publicAnalyticsEventSchema>;
export const publicAnalyticsBatchSchema = z.object({ contextToken: z.string().min(10).max(2000), events: z.array(publicAnalyticsEventSchema).min(1).max(20) }).strict();
export type PublicAnalyticsBatch = z.infer<typeof publicAnalyticsBatchSchema>;

const dateOnly = z.iso.date();
export const analyticsReportQuerySchema = z.object({ from: dateOnly.optional(), to: dateOnly.optional() }).refine((q) => !q.from || !q.to || q.from <= q.to, 'Start date must not follow end date');
export type AnalyticsReportQuery = z.infer<typeof analyticsReportQuerySchema>;
export const analyticsLandingRowSchema = z.object({
  landingVersionId: z.string(), landingName: z.string(), versionNumber: z.number().int(),
  sessions: z.number().int(), pageViews: z.number().int(), ctaClicks: z.number().int(),
  formStarts: z.number().int(), formSubmits: z.number().int(), leads: z.number().int(), qualified: z.number().int(), proposals: z.number().int(), won: z.number().int(),
  revenue: z.array(z.object({ currency: z.string(), amount: z.string() })),
});
export type AnalyticsLandingRow = z.infer<typeof analyticsLandingRowSchema>;
export const analyticsReportSchema = z.object({ from: z.string(), to: z.string(), rows: z.array(analyticsLandingRowSchema), measuredSince: z.string() });
export type AnalyticsReport = z.infer<typeof analyticsReportSchema>;

export const trackingHealthSchema = z.object({
  checkedAt: z.string(), windowHours: z.literal(24),
  firstParty: z.object({ events: z.number().int(), lastEventAt: z.string().nullable(), status: z.enum(['receiving', 'quiet', 'not_started']) }),
  clickIds: z.object({ captured: z.number().int(), lastCapturedAt: z.string().nullable() }),
  forms: z.object({ acceptedLeads: z.number().int(), lastAcceptedAt: z.string().nullable() }),
  external: z.object({ ga4: z.literal('not_configured'), googleAdsConversions: z.literal('not_configured'), linkedInConversions: z.literal('not_configured') }),
});
export type TrackingHealth = z.infer<typeof trackingHealthSchema>;

export const attributionTouchSchema = z.object({
  id: z.string(), occurredAt: z.string(), landingVersionId: z.string(), landingName: z.string(), channel: z.string(),
  utmSource: z.string().nullable(), utmMedium: z.string().nullable(), utmCampaign: z.string().nullable(), referrerOrigin: z.string().nullable(),
  first: z.boolean(), last: z.boolean(), creation: z.boolean(),
});
export const leadAttributionSchema = z.object({ visitorLinked: z.boolean(), sessionId: z.string().nullable(), touches: z.array(attributionTouchSchema) });
export type LeadAttribution = z.infer<typeof leadAttributionSchema>;
